import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Edit, Trash2, Plus, Search, X, CalendarIcon, History, Loader2 } from "lucide-react";
import { IconButtonWithTooltip } from "@/components/ui/icon-button-tooltip";
import { toast } from "@/hooks/use-toast";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { PatientProgramStatusTable } from "../../../components/program-status/PatientProgramStatusTable";
import { useProgramTypes } from "@/hooks/workflow/useProgramTypes";
import { useProgramTypeStatuses } from "@/hooks/workflow/useProgramTypeStatuses";
import LoaderComponent from "@/components/common/LoaderComponent";
import MultiSelect from "@/components/common/MultiSelect";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { workflowService } from "@/services/features/WorkflowService";
import { useAuth } from "@/contexts/AuthContext";
import { OperationsHistoryModal } from "@/v2/components/common/OperationsHistoryModal";
import { useProgramTypeAuditLog } from "@/hooks/workflow/useProgramTypeAuditLog";

const ProgramType = () => {
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("program");
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [date, setDate] = useState<Date>();
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProgramTypeId, setSelectedProgramTypeId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedTypeForHistory, setSelectedTypeForHistory] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const { user, token } = useAuth();

  const { programTypeAuditLogs, auditLogsLoading } = useProgramTypeAuditLog(
    selectedTypeForHistory?.id || null
  );

  const offset = (currentPage - 1) * entriesPerPage;
  const { programTypeStatuses } = useProgramTypeStatuses();
  const { 
    programTypes, 
    loading, 
    error, 
    totalCount, 
    refetch,
    filterType,
    searchValue,
    setFilterType,
    setSearchValue,
    handleSearch: performSearch,
    clearSearch
  } = useProgramTypes(entriesPerPage, offset);

  const totalPages = Math.ceil(totalCount / entriesPerPage);

  useEffect(() => {
    document.title = "Program Type | Admin";
  }, []);

  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  const toggleRowExpansion = (id: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleEdit = (id: string) => {
    navigate(`/admin/program-type/edit/${id}`);
  };

  const handleDelete = (id: string) => {
    setSelectedProgramTypeId(id);
    setDeleteDialogOpen(true);
  };

  const handleViewHistory = (id: number, name: string) => {
    setSelectedTypeForHistory({ id, name });
    setHistoryModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedProgramTypeId || !user?.id || !token) {
      toast({
        title: "Error",
        description: "Unable to delete program type",
        variant: "destructive",
      });
      return;
    }

    setIsDeleting(true);
    try {
      await workflowService.deleteProgramType(user.id, selectedProgramTypeId, token);
      
      toast({
        title: "Success",
        description: "Program type deleted successfully",
      });
      
      setDeleteDialogOpen(false);
      setSelectedProgramTypeId(null);
      refetch();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete program type",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    if (filterType === 'progrma_status' && selectedStatuses.length > 0) {
      setSearchValue(selectedStatuses.join(','));
    }
    performSearch();
  };

  const handleDateSelect = (selectedDate: Date | undefined) => {
    setDate(selectedDate);
    if (selectedDate) {
      setSearchValue(format(selectedDate, 'yyyy-MM-dd'));
    }
  };

  const handleClearSearch = () => {
    setDate(undefined);
    setSelectedStatuses([]);
    clearSearch();
    setCurrentPage(1);
  };

  const handleEntriesPerPageChange = (value: string) => {
    setEntriesPerPage(Number(value));
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleAddNew = () => {
    if (activeTab === "program") {
      navigate("/admin/program-type/add");
    } else {
      navigate("/admin/patient-program-status/add");
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  return (
    <>
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the program type.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                // Prevent Radix AlertDialog from auto-closing so the loader is visible
                e.preventDefault();
                void confirmDelete();
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Deleting...
                </span>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    <div id="program_type_page_container" className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 id="program_type_page_title" className="text-3xl font-bold text-foreground">Program Type & Patient Program Status</h1>
          <p className="text-muted-foreground mt-2">Configure and manage program types and patient program statuses</p>
        </div>
        <Button id="program_type_page_add_btn" onClick={handleAddNew} className="bg-yellow-500 hover:bg-yellow-600 text-black px-6">
          <Plus className="h-4 w-4" />
          {activeTab === "program" ? "Add New Program" : "Add New Patient Program Status"}
        </Button>
      </div>

      <Tabs defaultValue="program" value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex items-center justify-between">
          <TabsList id="program_type_tabs_list" className="grid w-fit grid-cols-2 bg-transparent p-0 h-auto">
            <TabsTrigger 
              id="program_type_tab_program"
              value="program" 
              className="data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none pb-4 px-6"
            >
              Program
            </TabsTrigger>
            <TabsTrigger 
              id="program_type_tab_patient_program_status"
              value="patient-program-status"
              className="data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none pb-4 px-6"
            >
              Patient Program Status
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent id="program_type_tab_content_program" value="program" className="mt-6">
          <div className="flex items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger id="program_type_filter_select" className="w-48">
                  <SelectValue placeholder="Select filter" />
                </SelectTrigger>
                <SelectContent className="bg-background">
                  <SelectItem id="program_type_filter_name" value="program_type">Program Name</SelectItem>
                  <SelectItem id="program_type_filter_date" value="created_datetime">Created Date</SelectItem>
                  <SelectItem id="program_type_filter_status" value="progrma_status">Applicable Statuses</SelectItem>
                </SelectContent>
              </Select>

              {filterType === 'created_datetime' ? (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-64 justify-start text-left font-normal",
                        !date && "text-muted-foreground"
                      )}
                    >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "dd/MM/yyyy") : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={handleDateSelect}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                    />
                  </PopoverContent>
                </Popover>
              ) : filterType === 'progrma_status' ? (
                <MultiSelect
                  options={Array.from(new Set(programTypeStatuses.map(s => s.program_status))).map(status => ({
                    label: status,
                    value: status
                  }))}
                  value={selectedStatuses}
                  onChange={(values) => setSelectedStatuses(values as string[])}
                  placeholder="Select statuses"
                  className="w-64"
                />
              ) : (
                <Input 
                  id="program_type_search_input"
                  className="w-64" 
                  placeholder={
                    filterType === 'program_type' ? "Enter program name" :
                    "Enter value"
                  }
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
              )}

              <Button 
                id="program_type_search_btn"
                className="bg-purple-600 hover:bg-purple-700 text-white px-6"
                onClick={handleSearch}
              >
                <Search className="h-4 w-4 mr-2" />
                Search
              </Button>

              {(searchValue || selectedStatuses.length > 0) && (
                <Button 
                  id="program_type_clear_btn"
                  variant="outline"
                  className="px-6"
                  onClick={handleClearSearch}
                >
                  <X className="h-4 w-4 mr-2" />
                  Clear
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Show</span>
              <Select value={entriesPerPage.toString()} onValueChange={handleEntriesPerPageChange}>
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="500">500</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">Entries</span>
            </div>
          </div>

          {loading ? (
            <LoaderComponent />
          ) : error ? (
            <div className="text-center py-8 text-destructive">{error}</div>
          ) : programTypes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No program types found</div>
          ) : (
            <>

          <div id="table-admin-program-types-container" className="rounded-md border">
            <Table id="table-admin-program-types">
              <TableHeader>
                <TableRow id="table-admin-program-types-header-row">
                  <TableHead id="header-cell-program-name">Program Name</TableHead>
                  <TableHead id="header-cell-created-date">Created Date</TableHead>
                  <TableHead id="header-cell-applicable-statuses">Applicable Statuses</TableHead>
                  <TableHead id="header-cell-actions" className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody id="table-admin-program-types-body">
                {programTypes.map((programType) => {
                  const statuses = Array.isArray(programType.progrma_status) 
                    ? programType.progrma_status 
                    : [];
                  
                  return (
                    <TableRow key={programType.program_type_id} id={`row-program-type-${programType.program_type_id}`}>
                      <TableCell id={`cell-program-type-${programType.program_type_id}-name`} className="font-medium">
                        {programType.program_type}
                      </TableCell>
                      <TableCell id={`cell-program-type-${programType.program_type_id}-created`}>{programType.created_datetime ? new Date(programType.created_datetime).toLocaleDateString('en-GB') : 'N/A'}</TableCell>
                      <TableCell id={`cell-program-type-${programType.program_type_id}-statuses`}>
                        <div className="flex flex-wrap gap-1">
                          {expandedRows.has(programType.program_type_id)
                            ? statuses.map((status, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  {status}
                                </Badge>
                              ))
                            : statuses.slice(0, 3).map((status, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  {status}
                                </Badge>
                              ))
                          }
                          {statuses.length > 3 && (
                            <Badge 
                              variant="outline" 
                              className="text-xs cursor-pointer hover:bg-accent"
                              onClick={() => toggleRowExpansion(programType.program_type_id)}
                            >
                              {expandedRows.has(programType.program_type_id) 
                                ? 'Show less' 
                                : `+${statuses.length - 3}`
                              }
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell id={`cell-program-type-${programType.program_type_id}-actions`} className="text-right">
                        <div className="flex justify-end gap-2">
                          <IconButtonWithTooltip
                            id={`btn-program-type-${programType.program_type_id}-history`}
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewHistory(
                              parseInt(programType.program_type_id), 
                              programType.program_type
                            )}
                            tooltip="View History"
                            icon={<History className="h-4 w-4" />}
                          />
                          <IconButtonWithTooltip
                            id={`btn-program-type-${programType.program_type_id}-edit`}
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(programType.program_type_id)}
                            tooltip="Edit"
                            icon={<Edit className="h-4 w-4" />}
                          />
                          <IconButtonWithTooltip
                            id={`btn-program-type-${programType.program_type_id}-delete`}
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(programType.program_type_id)}
                            className="text-destructive hover:text-destructive"
                            tooltip="Delete"
                            icon={<Trash2 className="h-4 w-4" />}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-muted-foreground whitespace-nowrap">
              Showing {offset + 1} to {Math.min(offset + entriesPerPage, totalCount)} of {totalCount} entries
            </div>
            
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious 
                    onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                    className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <PaginationItem key={page}>
                      <PaginationLink 
                        onClick={() => handlePageChange(page)}
                        className={currentPage === page 
                          ? "bg-purple-600 text-white hover:bg-purple-700 cursor-pointer" 
                          : "hover:bg-purple-100 cursor-pointer"
                        }
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}
                <PaginationItem>
                  <PaginationNext 
                    onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                    className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
          </>
          )}
        </TabsContent>

        <TabsContent value="patient-program-status" className="mt-6">
          <PatientProgramStatusTable />
        </TabsContent>
      </Tabs>

      <OperationsHistoryModal
        open={historyModalOpen}
        onOpenChange={setHistoryModalOpen}
        entityName={selectedTypeForHistory?.name || ''}
        entityType="Program Type"
        auditLogs={programTypeAuditLogs}
        loading={auditLogsLoading}
      />
    </div>
    </>
  );
};

export default ProgramType;